import { IChatStatsResponse, IChatMessageEvent } from "@finbackoffice/clientbff-client";
import {
    createContext,
    Dispatch,
    FC,
    PropsWithChildren,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useWebsocketUpdates } from "../hooks";
import { ClientBFFContext } from "./client.provider";
import { WebsocketContext } from "./websocket.provider";
import { AuthContext } from "./auth.provider";

type IProps = {
    handleChatUpdates: (payload: IChatMessageEvent, error: any) => void;
    token: string | null;
};

const SubscribeToChatMessages: FC<IProps> = ({ handleChatUpdates, token }) => {
    const params = useMemo(() => ({ token: token || "" }), [token]);

    useWebsocketUpdates("chat_message", handleChatUpdates, undefined, params);
    return null;
};

type IChatContext = {
    unreadMessages: IChatMessageEvent[];
    removeMessage: (chatId: number, messageIds: number[]) => void;
    getUnreadMessagesByChatId: (id: number) => IChatMessageEvent[];
    chatStats: IChatStatsResponse | null;
    setChatStats: Dispatch<SetStateAction<IChatStatsResponse | null>>;
};

export const ChatContext = createContext<IChatContext>(null as any);

export const ChatProvider: FC<PropsWithChildren> = ({ children }) => {
    const client = useContext(ClientBFFContext);
    const { userToken: token } = useContext(AuthContext);
    const { connected } = useContext(WebsocketContext);
    const isLoggedIn = token !== null && !!token;
    const [unreadMessages, setUnreadMessages] = useState<IChatMessageEvent[]>([]);
    const [chatStats, setChatStats] = useState<IChatStatsResponse | null>(null);

    useEffect(() => {
        const loadChatStats = async () => {
            const response = await client.getChatStats();
            setChatStats(response);
        };

        if (isLoggedIn) {
            loadChatStats();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn]);

    const removeMessage = useCallback(
        async (chatId: number, messageIds: number[]) => {
            setUnreadMessages((state) => state.filter((msg) => msg.chat.id !== chatId));
            await Promise.all(
                messageIds.map((messageId) =>
                    client.updateMessage(chatId, messageId, { is_read: true }),
                ),
            );
        },
        [client],
    );

    const getUnreadMessagesByChatId = useCallback(
        (id: number) => unreadMessages.filter((msg) => msg.chat.id === id),
        [unreadMessages],
    );

    const handleChatUpdates = useCallback((payload: IChatMessageEvent, error: any) => {
        if (!error) {
            if (payload.author === "Admin") {
                setUnreadMessages((state) => [...state, payload]);
            }
        } else {
            console.error(error);
        }
    }, []);

    useEffect(() => {
        if (!isLoggedIn) {
            setUnreadMessages([]);
            setChatStats(null);
        }
    }, [connected, isLoggedIn]);

    const value = useMemo(
        () => ({
            unreadMessages,
            removeMessage,
            getUnreadMessagesByChatId,
            chatStats,
            setChatStats,
        }),
        [getUnreadMessagesByChatId, removeMessage, unreadMessages, chatStats, setChatStats],
    );

    return (
        <ChatContext.Provider value={value}>
            {isLoggedIn && (
                <SubscribeToChatMessages handleChatUpdates={handleChatUpdates} token={token} />
            )}
            {children}
        </ChatContext.Provider>
    );
};
